import { useMemo } from 'react';
import { Permission } from '../constants/permissions';
import UserRole from '../constants/user-role';
import { FeatureType, useFeatures } from './features';
import useAuthContext from './useAuthContext';
import { hasPermission, hasAnyPermission, hasAllPermissions, hasRole, hasFeature } from '../utils/permissionUtils';

/**
 * Hook for checking permissions within components
 *
 * @example
 * function MyComponent() {
 *   const { hasPermission, hasRole, hasFeature } = usePermission();
 *
 *   if (!hasPermission(Permission.VIEW_DASHBOARD)) {
 *     return <AccessDenied />;
 *   }
 *
 *   return <Dashboard />;
 * }
 */
export function usePermission() {
  const { state } = useAuthContext();
  const featuresStore = useFeatures();

  return useMemo(
    () => ({
      /**
       * Check if the current user has a specific permission
       */
      hasPermission: (permission: Permission) => hasPermission(state.role, permission, featuresStore),

      /**
       * Check if the current user has any of the specified permissions
       */
      hasAnyPermission: (permissions: Permission[]) => hasAnyPermission(state.role, permissions, featuresStore),

      /**
       * Check if the current user has all of the specified permissions
       */
      hasAllPermissions: (permissions: Permission[]) => hasAllPermissions(state.role, permissions, featuresStore),

      /**
       * Check if the current user has a specific role
       */
      hasRole: (roles: UserRole | UserRole[]) => hasRole(state.role, roles),

      /**
       * Check if a specific feature is enabled
       */
      hasFeature: (feature: FeatureType) => hasFeature(feature, featuresStore),

      /**
       * Get the current user's role
       */
      userRole: state.role,

      /**
       * Check if the user is authenticated
       */
      isAuthenticated: state.isLoggedIn
    }),
    [state.role, state.isLoggedIn, featuresStore]
  );
}

export default usePermission;
